import { PureComponent } from "react";
import { v4 as uuid } from "uuid";

import { Icon, IconType } from "components/Icon/Icon";
import { MultilineTooltipTemplate } from "components/Tooltip/MultilineTooltipTemplate";
import { Tooltip } from "components/Tooltip/Tooltip";
import { hasValidationError, isCriticalValidationError } from "models/encounterErrors";
import { getClassNameAsString } from "utils/getClassNameAsString";

import type { ValidationErrors } from "models/encounterErrors";

import "./ErrorWrap.scss";

export interface ErrorWrapProps {
  id?: string;
  error?: ValidationErrors;
  className?: string;
  withLabel?: boolean;
}

export class ErrorWrap extends PureComponent<ErrorWrapProps> {
  el: HTMLInputElement;
  targetElement: HTMLElement | null = null;

  setRef = (ref) => {
    this.targetElement = ref;
  };

  // all client side errors are simple strings (do we want special flag?)
  collectClientSideCriticalErrors = (error?: ValidationErrors) => {
    if (!error) {
      return undefined;
    }

    if (!Array.isArray(error)) {
      return typeof error === "string" ? error : undefined;
    }

    const errors = error.filter((item) => typeof item === "string");

    return errors.length > 0 ? errors : undefined;
  };

  render() {
    const id = this.props.id || `id-${uuid()}`;
    const filter = (el: HTMLElement) => {
      const className = getClassNameAsString(el);
      return className.split(" ").includes("icon");
    };
    const { error } = this.props;
    const hasError = typeof error !== "undefined" && hasValidationError(error);
    let wrapperClass =
      hasError ?
        "tc-error-wrap form-group inform-outer-wrap-new has-error"
      : "tc-error-wrap form-group inform-outer-wrap-new";
    if (this.props.className) {
      wrapperClass += ` ${this.props.className}`;
    }
    if (this.props.withLabel) {
      wrapperClass += " with-label";
    }

    const clientSideCriticalErrors = this.collectClientSideCriticalErrors(this.props.error);
    const hasClientSideErrors = !!clientSideCriticalErrors;

    return (
      <div id={id} className={wrapperClass}>
        {/* client side errors tooltip. always visible if we have client side errors. show only client side errors */}
        <Tooltip
          filter={filter}
          anchorElement="target"
          openDelay={hasClientSideErrors ? 5 : 500}
          showCallout={false}
          position="auto"
          content={() => (
            <MultilineTooltipTemplate
              content={hasClientSideErrors ? clientSideCriticalErrors : error}
            />
          )}
          targetElement={hasClientSideErrors ? this.targetElement : undefined}
        >
          {/* usual tooltip. visible only if we have not client side errors */}
          <div className="tc-error-wrap-inner">
            <Icon
              type={isCriticalValidationError(error) ? IconType.Error : IconType.Warning}
              forceTooltip
              setRef={this.setRef}
            />
            {this.props.children}
          </div>
        </Tooltip>
      </div>
    );
  }
}
